import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdatePreboardingForm } from '@/hooks/use-preboarding-forms';
import type { PreboardingForm, PreboardingFormWelcomeConfig } from '@hygge/shared';
import { CoverImageField } from '../cover-image-field';
import { RichVariableEditor } from '../rich-variable-editor';
import { SaveStatusIndicator } from '../save-status-indicator';
import { useDraftAutosave } from '../use-draft-autosave';
import { WelcomePreview } from '../welcome-preview';
import type { PreboardingFormStepProps } from './setup-emails-step';

function toDraft(form: PreboardingForm): PreboardingFormWelcomeConfig {
  const w = form.welcomeConfig;
  return {
    coverImageUrl: w?.coverImageUrl ?? null,
    greetingTitle: w?.greetingTitle ?? 'Welcome to the team!',
    welcomeMessage: w?.welcomeMessage ?? '',
  };
}

// Step 2 — Welcome Screen (HW-556). Configures the external landing page a
// candidate sees first: cover image, greeting title, and welcome message, with
// a live preview. Autosaves the draft.
export function WelcomeStep({ form }: PreboardingFormStepProps) {
  const updateMutation = useUpdatePreboardingForm({ suppressGlobalToast: true });

  const { draft, applyChange, commitSaved, status, lastSavedAt, saveNow } =
    useDraftAutosave<PreboardingFormWelcomeConfig>({
      initial: toDraft(form),
      onSave: async (next) => {
        await updateMutation.mutateAsync({ id: form.id, input: { welcomeConfig: next } });
      },
    });

  // The cover endpoints persist welcomeConfig server-side and return the form —
  // adopt it without scheduling a redundant save.
  const onCoverUpdated = (updated: PreboardingForm) => commitSaved(toDraft(updated));

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_minmax(320px,420px)]">
      {/* Configuration */}
      <div className="space-y-6">
        <section className="space-y-3 rounded-xl border border-border bg-card p-5">
          <h3 className="text-sm font-semibold text-foreground">Cover image</h3>
          <CoverImageField
            formId={form.id}
            coverImageUrl={draft.coverImageUrl}
            onUpdated={onCoverUpdated}
          />
        </section>

        <section className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div className="space-y-1.5">
            <Label htmlFor="greeting-title">Greeting title</Label>
            <Input
              id="greeting-title"
              value={draft.greetingTitle}
              placeholder="Welcome to the team!"
              onChange={(e) => applyChange({ ...draft, greetingTitle: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Welcome message</Label>
            <RichVariableEditor
              content={draft.welcomeMessage}
              onChange={(html) => applyChange({ ...draft, welcomeMessage: html })}
              placeholder="Hi {{employeeFirstName}}, we're thrilled to have you at {{companyName}}…"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            A “Get Started” button is added to the bottom of the screen automatically.
          </p>
        </section>

        <div className="flex justify-end">
          <SaveStatusIndicator status={status} lastSavedAt={lastSavedAt} onRetry={saveNow} />
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-4 lg:self-start">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Preview
        </p>
        <WelcomePreview
          coverImageUrl={draft.coverImageUrl}
          greetingTitle={draft.greetingTitle}
          welcomeMessage={draft.welcomeMessage}
        />
      </div>
    </div>
  );
}
